"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  Stethoscope,
  Sparkles,
  Crown,
  GraduationCap,
  ShieldCheck,
  Brain,
  Activity,
  Lock,
  ArrowRight,
  Building2,
  Globe2,
  Mic,
  Briefcase,
  GitBranch,
  Users,
  Award,
  Target,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { TimelineBlock } from "@/lib/blocks/timeline-block";
import { CounterBlock } from "@/lib/blocks/counter-block";
import { FeatureGridBlock } from "@/lib/blocks/feature-grid-block";
import { TrendingUp, BookOpen } from "lucide-react";

const LIFE_TIMELINE = [
  { period: "1980s", title: "Formative Years in Delhi", body: "Delhi instills the survival skills no classroom teaches — operating under constraints, deciding quickly and correctly, carrying the weight of decisions without hesitation.", accent: "red" as const },
  { period: "1990s", title: "MBBS · Gynaecology · Delhi", body: "The medical degree is the installation of an operating system. Six principles drilled into muscle memory through clinical practice where failure is measured in human lives.", accent: "purple" as const },
  { period: "Clinical", title: "The Delivery Room Years", body: "Practicing medicine in one of the most demanding healthcare ecosystems on earth. Composure under absolute pressure. Credibility under fire. The first boardroom, in miniature.", accent: "cyan" as const },
  { period: "Year 01", title: "The Pivot to Sovereign Security", body: "Co-founding the security work with the team. Encrypted private networks for governments and their highest offices. Zero security incidents. The trust that makes everything else possible.", accent: "emerald" as const },
  { period: "Year 03", title: "Intelligence & Foresight", body: "The transition from defensive security to proactive intelligence. Eighty-nine percent prediction accuracy. The organization gains the ability to see what is about to happen.", accent: "amber-gold" as const },
  { period: "Year 05", title: "Perception & Governance", body: "The Penta-P framework crystallizes. Perception, Privacy, Politics, Policing, Policy as one closed-loop system. Country-level political campaigns. First verified electoral victories.", accent: "denim" as const },
  { period: "Year 07", title: "The Digital Conglomerate", body: "MaxiMize Infinium. Nine proprietary platforms. Sixty-plus integrated services. Eighteen countries. Three continents. Over three hundred elite clients served.", accent: "red" as const },
  { period: "Today", title: "COO at Sovereign Scale", body: "The operational command position. The decade of building made visible. The clinical method proven across every platform, every geography, every engagement.", accent: "purple" as const },
];

const KEY_NUMBERS = [
  { value: 18, suffix: "", label: "Countries · Active Operations", description: "Three continents of sovereign-grade presence.", accent: "red" as const, icon: <Globe2 className="size-5" /> },
  { value: 300, suffix: "+", label: "Elite Clients Served", description: "National-security-level confidentiality across every engagement.", accent: "purple" as const, icon: <ShieldCheck className="size-5" /> },
  { value: 15, suffix: " / 18", label: "Campaign Victories", description: "An eighty-three point three percent verified success rate.", accent: "cyan" as const, icon: <Target className="size-5" /> },
  { value: 900, suffix: "M+", label: "Voters Reached", description: "Across twenty-two official languages and hundreds of thousands of polling booths.", accent: "emerald" as const, icon: <Users className="size-5" /> },
  { value: 0, suffix: "", label: "Security Incidents", description: "Across fifteen-plus years of sovereign-grade operations.", accent: "amber-gold" as const, icon: <Lock className="size-5" /> },
  { value: 99.9999, suffix: "%", label: "Operational Uptime", description: "A maximum of thirty-one point five seconds of downtime per year.", accent: "denim" as const, icon: <TrendingUp className="size-5" /> },
];

const FOCUS_AREAS = [
  {
    title: "The Discipline of Execution",
    body: "Excellence as an engineered system, not an aspirational value. The mechanisms — protocols, checklists, reviews, outcome accountability — that convert ambition into verified results. The five-step methodology governs every engagement.",
    icon: <Target className="size-6" />,
    accent: "red" as const,
  },
  {
    title: "Leadership as Diagnosis",
    body: "The clinical method applied to organizational leadership. Triage, differential diagnosis, protocol, judgment, outcome ownership, and the discipline of doing no harm under pressure. The six principles scaled across eighteen countries.",
    icon: <Stethoscope className="size-6" />,
    accent: "purple" as const,
  },
  {
    title: "The 70% Project",
    body: "The operating case for women in operational leadership. Not a diversity initiative — a talent strategy with a decade of evidence. Verified through fifteen campaign victories, over three hundred elite clients, and zero security incidents.",
    icon: <Users className="size-6" />,
    accent: "cyan" as const,
  },
  {
    title: "Building Things That Last",
    body: "Entrepreneurship, governance delivery, and the discipline of constructing consequential organizations in systems, not charisma. A single organization, co-built and sustained for ten years, through every global disruption.",
    icon: <Building2 className="size-6" />,
    accent: "emerald" as const,
  },
  {
    title: "Governance and Public Policy",
    body: "Digital governance transformation, citizen engagement, and policy implementation through the governance platforms. The operational layer that converts political authority into measurable public outcomes.",
    icon: <ShieldCheck className="size-6" />,
    accent: "amber-gold" as const,
  },
  {
    title: "Strategic Intelligence",
    body: "Real-time intelligence gathering, predictive analytics, and decision-ready information through the intelligence engine and the ground-truth intelligence platform. Foresight, not hindsight.",
    icon: <Globe2 className="size-6" />,
    accent: "denim" as const,
  },
];

const PRINCIPLES = [
  { num: "01", title: "Differential Diagnosis Before Action", body: "A physician does not treat the first symptom. The discipline is to ask: what else could this be? Most organizational failures trace to leaders who treated the symptom while the disease progressed undetected. The clinical method prevents this.", accent: "red" },
  { num: "02", title: "Triage Under Absolute Pressure", body: "When every patient screams urgency, the physician must rank: what is fatal now, what can wait, what must be monitored, what can be safely ignored. Survival architecture applied to sovereign-scale operations.", accent: "purple" },
  { num: "03", title: "Protocol with Judgment", body: "Protocols carry through the ninety-five percent of cases that follow the pattern. The remaining five percent require clinical judgment — the ability to deviate from protocol when the situation demands it.", accent: "cyan" },
  { num: "04", title: "Outcome Accountability", body: "A physician does not receive credit for how hard the attempt was. The patient lived or did not. Organizations have lost this accountability. Dr. Kush restores it — every engagement measured by verified outcomes.", accent: "emerald" },
  { num: "05", title: "Composure as Infrastructure", body: "The delivery room teaches that the senior person's state determines the team's state. The discipline of maintaining internal composure so that the team can function is not emotional intelligence — it is operational infrastructure.", accent: "amber-gold" },
  { num: "06", title: "Do No Harm", body: "The oldest principle in medicine. Under pressure, most leaders make the situation worse. The discipline of not making it worse is the most underrated operational skill in leadership.", accent: "denim" },
];

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
};

const PHASES = [
  { num: "01", title: "Information Privacy", body: "Secure, encrypted private networks for governments and their highest offices. The foundation of trust earned through zero security incidents across every engagement. The cultural DNA established here fueled every subsequent expansion.", accent: "red" },
  { num: "02", title: "Intelligence & Listening", body: "Network Monitoring, Digital Listening, and Intelligence Analysis — driven by clients who recognized the same discipline could be applied to understanding the digital environment around their communications.", accent: "purple" },
  { num: "03", title: "Perception & Governance", body: "The invitation to apply methodologies to broader challenges: Perception Management, Governance Support, and country-level Political Campaigns. The Penta-P framework crystallized here as a closed-loop system.", accent: "cyan" },
  { num: "04", title: "The Digital Conglomerate", body: "The evolution into MaxiMize Infinium — the World&apos;s First Digital Conglomerate for Strategic Sovereignty. Sixty-plus integrated services. Eighteen countries. Three continents. Over three hundred elite clients served.", accent: "emerald" },
];

const FOUNDATION = [
  { icon: GraduationCap, title: "Delhi — Where the Discipline Was Forged", body: "One of the most demanding healthcare ecosystems on earth, serving a population of hundreds of millions with finite resources. The environment instills survival skills that no classroom can teach — operating under constraints, deciding quickly and correctly, carrying the weight of decisions without hesitation.", accent: "red" },
  { icon: Stethoscope, title: "The MBBS — Installation of an Operating System", body: "The medical degree with specialization in Gynaecology was not a professional qualification. It was the installation of a complete operating system — one that would prove more effective at sovereign scale than any business school curriculum could produce.", accent: "purple" },
  { icon: ShieldCheck, title: "Credibility Under Fire", body: "The years of clinical practice built something no credential can confer. When Dr. Jyoti Kush later entered board rooms where the stakes were national, the quiet authority of someone who had managed life-and-death decisions was carried forward.", accent: "emerald" },
];

const TRIAD = [
  { icon: Stethoscope, title: "Medicine", body: "The method. Evidence over intuition, protocol under pressure, triage when everything is urgent, and accountability not for effort but for outcome. The six principles form the actual operating system.", accent: "red" },
  { icon: Brain, title: "Technology", body: "The scale. The integrated platform ecosystem, built in-house over fifteen-plus years, processes millions of data points, monitors two hundred-plus digital platforms, and orchestrates operations across continents. Zero third-party dependencies.", accent: "cyan" },
  { icon: Globe2, title: "Politics", body: "The stakes. When the client is a sovereign government, a royal household, a defense ministry, or a political leader facing an existential contest, the work shapes nations, economies, and the lives of millions.", accent: "amber-gold" },
];

const COO_RESPONSIBILITIES = [
  { icon: Activity, title: "Platform Operations", body: "Oversight of the integrated platform ecosystem — ensuring the platforms operate as a unified system rather than disconnected tools. Every platform owned. Every capability proprietary." },
  { icon: Building2, title: "Governance Programs", body: "Design and execution of governance transformation initiatives for sovereign institutions — from policy implementation tracking to citizen engagement to service delivery optimization." },
  { icon: Target, title: "Campaign Operations", body: "End-to-end operational coordination of political campaigns, perception management engagements, and strategic sovereignty operations across eighteen countries. Fifteen victories out of eighteen engagements." },
  { icon: Users, title: "Team Building", body: "Assembly and leadership of cross-functional, multidisciplinary teams deployed across eighteen countries. Teams are over seventy percent women by selection and by results." },
  { icon: Briefcase, title: "Client Relationships", body: "Oversight of engagement quality across over three hundred elite clients. Every engagement follows the five-step methodology: Discovery, Research, Monitor, Analysis, Execution." },
  { icon: Lock, title: "Infrastructure & Security", body: "Oversight of the operational infrastructure that delivers ninety-nine point nine nine nine nine percent uptime. Zero security incidents across fifteen-plus years." },
];

export default function BiographyPage() {
  return (
    <>
      <PageHero
        icon={Stethoscope}
        overline="The Complete Story"
        overlineClassName="text-red"
        title={
          <>
            The Complete Story of <span className="text-gradient-premium">Dr. Jyoti Kush.</span>
          </>
        }
        subtitle="From formative years in Delhi through medical school, from the delivery room to the boardroom, from clinical practice to the operational command of a sovereign-grade digital conglomerate — every phase documented, every transition explained, every principle traced to its origin."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "About", href: "/about/" }, { label: "Biography" }]}
        badge="Physician · Operator · Architect"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-5`}>
            <Sparkles className="size-3.5 text-purple" />
            <span className="text-overline text-purple">Executive Summary</span>
          </div>
          <h2 className="text-display-md mb-5">A career that defies every conventional category.</h2>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            Dr. Jyoti Kush is a physician-turned-executive whose career defies every conventional category. Holding an MBBS with a specialization in Gynaecology from Delhi, the transition was made from clinical medicine into the operational leadership of a digital conglomerate that serves the most powerful entities on the planet — governments, royal families, political leaders, defense agencies, and Fortune-scale corporations across thirty-plus countries.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            As Chief Operating Officer, the operational mandate spans the integrated platform ecosystem — sixty-plus integrated services across five interconnected domains — ensuring they operate in coordinated harmony across every engagement. The work is not administrative. It is the operational execution layer that translates technological capability into client outcomes.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed">
            The story matters because it is not a conventional executive narrative. It is evidence that the skills forged in the most demanding clinical environments scale from the delivery room to the boardroom of nations. This is the full biography of how a gynaecologist from Delhi became the operational architect of a sovereign-grade digital conglomerate — and what that journey reveals about excellence, leadership, and the talent the world overlooks.
          </p>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <BookOpen className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">Interactive Life Timeline</span>
          </div>
          <h2 className="text-display-md mb-4">The arc in eight milestones.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            From formative years in Delhi through the medical training, the pivot, the four phases, and the current operational command. Every transition explained. Every principle traced to its origin.
          </p>
        </div>
        <TimelineBlock milestones={LIFE_TIMELINE} orientation="horizontal" />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-4`}>
            <Target className="size-3.5 text-red" />
            <span className="text-overline text-red">Key Numbers</span>
          </div>
          <h2 className="text-display-md mb-4">The verified record, in numbers.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Every metric below traces to operational data — not projections, not targets, not marketing claims. The verified outcomes of a decade of sovereign-scale operations.
          </p>
        </div>
        <CounterBlock stats={KEY_NUMBERS} columns={3} />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <GraduationCap className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">The Foundation</span>
          </div>
          <h2 className="text-display-md mb-4">Formative years and the seeds of discipline.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The story begins in Delhi, one of the most demanding environments on earth. The years of clinical training built the operating system that defines the leadership approach.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {FOUNDATION.map((e, i) => {
            const a = ACCENT[e.accent];
            return (
              <motion.div
                key={e.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className={`glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border}`}
              >
                <div className="size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center mb-4 shadow-lg">
                  <e.icon className="size-6 text-white" />
                </div>
                <h3 className={`text-heading-md mb-3 ${a.text}`}>{e.title}</h3>
                <p className="text-body-sm text-muted-foreground leading-relaxed">{e.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <Stethoscope className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">The Clinical Operating System</span>
          </div>
          <h2 className="text-display-md mb-4">Six principles learned in a delivery room.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The medical training was not a stepping stone. It was the installation of a complete operating system — one proven more effective at sovereign scale than any business school curriculum.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {PRINCIPLES.map((p, i) => {
            const a = ACCENT[p.accent];
            return (
              <motion.div
                key={p.num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group relative glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} overflow-hidden`}
              >
                <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                <div className="relative">
                  <div className={`text-display-sm font-bold ${a.text} opacity-30 leading-none mb-3`}>{p.num}</div>
                  <h3 className={`text-heading-md mb-3 ${a.text}`}>{p.title}</h3>
                  <p className="text-body-sm text-muted-foreground leading-relaxed">{p.body}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <GitBranch className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">The Decade of Building</span>
          </div>
          <h2 className="text-display-md mb-4">Four phases. One operational constant.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The evolution from a security-focused consultancy into a full-spectrum Digital Conglomerate. Dr. Jyoti Kush has been the operational constant — the person ensuring complexity was managed with the same clinical discipline that characterized the original security work.
          </p>
        </div>
        <div className="space-y-4">
          {PHASES.map((p, i) => {
            const a = ACCENT[p.accent];
            return (
              <motion.div
                key={p.num}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className={`group glass-card rounded-2xl p-6 lg:p-7 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="grid lg:grid-cols-[auto_1fr_auto] gap-6 items-center">
                  <div className={`size-16 rounded-2xl bg-gradient-sovereign flex items-center justify-center shadow-xl ring-2 ${a.ring} group-hover:scale-110 group-hover:rotate-3 transition-all`}>
                    <span className="text-display-sm font-bold text-white">{p.num}</span>
                  </div>
                  <div>
                    <span className={`text-overline ${a.text}`}>Phase {p.num}</span>
                    <h3 className={`text-display-sm mb-2 ${a.text}`}>{p.title}</h3>
                    <p className="text-body-md text-muted-foreground leading-relaxed">{p.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-5`}>
              <Crown className="size-3.5 text-denim" />
              <span className="text-overline text-denim">The Triple Threat</span>
            </div>
            <h2 className="text-display-md mb-4">The intersection no one else occupies.</h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              Most leaders specialize in a single domain. The career operates across three — and the intersection is where the value is unmatched.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              The triple threat is not three separate capabilities. It is one integrated operating method — clinical rigor applied to technological systems under political-grade stakes. No other operator at this scale brings a physician&apos;s discipline to sovereign-level execution.
            </p>
          </div>
          <div className="space-y-3">
            {TRIAD.map((t, i) => {
              const a = ACCENT[t.accent];
              return (
                <motion.div
                  key={t.title}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
                >
                  <div className="flex items-start gap-3.5">
                    <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                      <t.icon className="size-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className={`text-heading-md mb-1.5 ${a.text}`}>{t.title}</h3>
                      <p className="text-body-sm text-muted-foreground leading-relaxed">{t.body}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.lime.pill} mb-4`}>
            <Briefcase className="size-3.5 text-lime" />
            <span className="text-overline text-lime">The Role of Chief Operating Officer</span>
          </div>
          <h2 className="text-display-md mb-4">What the COO role actually means.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The COO title is common. What Dr. Jyoti Kush does with it is not. The role is not administrative — it is the operational execution layer that translates technological capability into client outcomes.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {COO_RESPONSIBILITIES.map((r, i) => (
            <motion.div
              key={r.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ duration: 0.4, delay: i * 0.06 }}
              className="glass-card rounded-2xl p-5 bg-gradient-to-br from-lime/10 to-transparent border border-lime/20 hover:scale-[1.01] transition-all"
            >
              <div className="size-10 rounded-lg bg-gradient-sovereign flex items-center justify-center mb-3 shrink-0">
                <r.icon className="size-5 text-white" />
              </div>
              <h3 className="text-heading-md mb-2 text-lime">{r.title}</h3>
              <p className="text-caption text-muted-foreground leading-relaxed">{r.body}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-5`}>
            <Globe2 className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">Global Stature</span>
          </div>
          <h2 className="text-display-md mb-4">Operating at the level of nations.</h2>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            The work has placed Dr. Jyoti Kush alongside governments and global leaders in over thirty countries, with active operations across eighteen countries on three continents — Africa, the Americas, and Asia. This is not travel. This is not conference attendance. This is operational presence — the sustained execution of intelligence, perception, governance, and security programs at the level where the stakes are national, the clients are sovereign, and the margin for error is zero.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            Active operations span seven primary regions: Africa, Central America, South America, Central Asia, Southern Asia, the Middle East, and the GCC. Additional presence in North America — with engagements across forty-five United States — and Europe through strategic advisory. The teams span fifteen-plus languages, ensuring that operational capability is never constrained by linguistic boundaries. Presence at the Nobel Peace Prize and the World Economic Forum places the work alongside those operating at the highest levels of international consequence.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed">
            The global stature was earned through a decade of operational delivery across the world&apos;s most demanding environments, where the consequences of failure are measured in national stability and the rewards of success are measured in verified outcomes. The organization did not merely survive the COVID era — it grew, excelled, and evolved while most organizations contracted, because the operational infrastructure was built to function regardless of external conditions.
          </p>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Target className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">Areas of Focus</span>
          </div>
          <h2 className="text-display-md mb-4">Where the expertise concentrates.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Six domains where the operating system produces the strongest outcomes. Each grounded in verified operational data, each scaled from the delivery room to sovereign scale.
          </p>
        </div>
        <FeatureGridBlock features={FOCUS_AREAS} columns={3} />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-5`}>
            <Sparkles className="size-3.5 text-denim" />
            <span className="text-overline text-denim">Why This Story Matters</span>
          </div>
          <h2 className="text-display-md mb-4">A proof of concept for the clinical method at scale.</h2>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            The biography matters because it challenges the fundamental assumption of how sovereign-scale organizations should be led. The conventional path runs through business schools, consulting firms, and corporate hierarchies. Dr. Kush&apos;s path ran through a delivery room in Delhi — where the margin between success and catastrophe is measured in minutes, and where the discipline of evidence, protocol, triage, and outcome accountability is not a theoretical framework but a survival requirement.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            The clinical method produces outcomes that business schools cannot replicate. The evidence is not theoretical — it is operational: fifteen victories out of eighteen campaigns, nine hundred million voters reached, over three hundred elite clients served, zero security incidents across fifteen-plus years, and ninety-nine point nine nine nine nine percent uptime. The six principles — differential diagnosis, triage, protocol with judgment, outcome accountability, composure as infrastructure, and do no harm — do not break at scale. They compound.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed">
            This is the significance of the biography. Not that a gynaecologist became a COO. But that the discipline forged in clinical practice — when applied with the same rigor to organizations operating at sovereign scale — produces outcomes that no conventional executive training can replicate. The delivery room was the first boardroom. The method was the same. The stakes were different. The outcomes were the same: verified, measurable, and impossible to ignore.
          </p>
        </div>
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-red/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-purple/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Sparkles className="size-10 text-red mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Continue the journey.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              The biography is one chapter. Explore the four phases of the career, the verified credentials, the recognition, and the philosophy that governs every engagement.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/about/journey/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                The Four Phases
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/about/credentials/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <Award className="size-4" />
                View Credentials
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "About Dr. Jyoti Kush", description: "The full story. The physician who became an operator at sovereign scale.", href: "/about/", icon: Crown, accent: "red" },
          { title: "The Journey", description: "Four phases: physician → privacy → decade → operator.", href: "/about/journey/", icon: GitBranch, accent: "purple" },
          { title: "Philosophy & Vision", description: "The clinical method applied to organizations.", href: "/about/philosophy/", icon: Brain, accent: "emerald" },
          { title: "Recognition", description: "Nobel Peace Prize, WEF, and sovereign stages.", href: "/about/recognition/", icon: Award, accent: "amber-gold" },
        ]}
      />
    </>
  );
}
